import type { Polygon } from "geojson";
import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

import type { ItinerarySegment } from "@mountayaapp/types/api/api";

import type { ItinerarySelection } from "./itinerary";

/**
 * COLLAB_PEER_COLORS is the fixed palette a collaborator is assigned a hue from,
 * spelled as Mantine color NAMES so the UI can paint a peer's cursor / selection
 * with `var(--mantine-color-<name>-6)` without a bespoke color scale. Eight
 * distinct, high-contrast hues so a small session rarely collides; the
 * assignment is deterministic per user id (see {@link collabPeerColor}) so the
 * same collaborator keeps the same color across reconnects and across every
 * viewer's screen.
 */
export const COLLAB_PEER_COLORS = [
  "blue",
  "grape",
  "teal",
  "orange",
  "indigo",
  "pink",
  "cyan",
  "lime",
] as const;

/**
 * collabPeerColor maps a user id onto a stable palette entry with a small
 * deterministic string hash, so a collaborator's color is identical on every
 * screen and survives a reconnect (the hash depends only on the id, never on
 * join order). The 32-bit accumulator is folded to an unsigned value before the
 * modulo so a negative hash never lands a negative index. Returns a Mantine
 * color name the UI expands into a CSS variable.
 */
export const collabPeerColor = (userId: string): string => {
  let hash = 0;
  for (let index = 0; index < userId.length; index += 1) {
    hash = (hash * 31 + userId.charCodeAt(index)) | 0;
  }

  const paletteIndex = (hash >>> 0) % COLLAB_PEER_COLORS.length;

  return COLLAB_PEER_COLORS[paletteIndex] ?? COLLAB_PEER_COLORS[0];
};

/**
 * collabLockKindSchema is the entity family a live lock or a live peer draft is
 * keyed on. Zod-first because it is shared with the collab lock / draft tool
 * inputs and validated when the collaboration session relays a remote frame.
 *
 * It is core's structural twin of the collaboration transport's entity-kind enum —
 * core cannot import the collab package — kept in lockstep by a parity test, so each
 * member carries the same wire spelling the backend keys its lock registry on
 * (`segment_group`, not a shorter local name). The set is WIDER than the family the
 * map can draw a live ghost for: a segment group is lockable while a peer renames it,
 * but it owns no geometry to preview.
 */
export const collabLockKindSchema = z.enum(["segment", "segment_group", "waypoint", "zone"]);

/**
 * CollabLockKind is the entity family a lock or draft targets, derived from
 * {@link collabLockKindSchema}.
 */
export type CollabLockKind = z.infer<typeof collabLockKindSchema>;

/**
 * collabLockKey composes the `"{kind}/{id}"` string the lock map and the peer
 * draft map are both keyed on, so a lock on a segment and a draft on the same
 * segment resolve to the same slot vocabulary. The single key builder keeps the
 * two maps' key shape from drifting.
 */
export const collabLockKey = (kind: CollabLockKind, id: string): string => `${kind}/${id}`;

/**
 * collabResourceKindSchema is the collection a collaboration EDITOR session
 * follows — an itinerary or a zone — the outer bucket each session's collaborators,
 * locks, and drafts are scoped under, so two concurrent sessions (an itinerary and
 * a zone open at once) never overwrite each other's live picture. The value is the
 * wire path segment (`itineraries` / `zones`). It is core's own structural twin of
 * the collaboration transport's resource kind — core cannot import the collab
 * package — kept in lockstep by a parity test. Zod-first because it is shared with
 * the collab tool inputs and validated when the session relays a remote frame.
 */
export const collabResourceKindSchema = z.enum(["itineraries", "zones"]);

/**
 * CollabResourceKind is the collection a collaboration editor session follows,
 * derived from {@link collabResourceKindSchema}.
 */
export type CollabResourceKind = z.infer<typeof collabResourceKindSchema>;

/**
 * collabResourceSchema is the `{ id, kind }` handle naming the editor session a
 * collaboration frame belongs to. It scopes every receive-side collab tool input
 * so a frame lands in exactly its resource's slice — an itinerary snapshot never
 * touches a zone session's roster and vice versa. Zod-first because it is shared
 * with the collab tool inputs and validated when the session relays a remote frame.
 */
export const collabResourceSchema = z.object({ id: z.string(), kind: collabResourceKindSchema });

/**
 * CollabResource is the itinerary or zone a collaboration editor session follows,
 * derived from {@link collabResourceSchema}.
 */
export type CollabResource = z.infer<typeof collabResourceSchema>;

/**
 * collabResourceKey composes the `"{kind}/{id}"` string the per-resource collab
 * map is keyed on, so an itinerary session and a zone session resolve to distinct
 * slots and never share a peer/lock/draft picture. The single key builder keeps
 * the outer map's key shape from drifting, the way {@link collabLockKey} keeps the
 * inner lock/draft maps' key shape from drifting.
 */
export const collabResourceKey = (resource: CollabResource): string =>
  `${resource.kind}/${resource.id}`;

/**
 * collabLockEntrySchema is one held lock in a lock snapshot: the locked entity
 * (kind + id) and the collaborator holding it. Zod-first because it is shared with
 * the lock-snapshot tool input and validated when the collaboration session relays
 * the authoritative held-lock set.
 */
export const collabLockEntrySchema = z.object({
  id: z.string(),
  kind: collabLockKindSchema,
  userId: z.string(),
});

/**
 * CollabLockEntry is one held lock — the locked entity plus its holder — derived
 * from {@link collabLockEntrySchema}.
 */
export type CollabLockEntry = z.infer<typeof collabLockEntrySchema>;

/**
 * collabPeerCursorSchema is a collaborator's live map cursor position in map
 * coordinates. Zod-first because it is shared with the peer-cursor tool input
 * and validated when the collaboration session relays a remote pointer move.
 */
export const collabPeerCursorSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

/**
 * CollabPeerCursor is a collaborator's live [lng, lat] cursor, derived from
 * {@link collabPeerCursorSchema}.
 */
export type CollabPeerCursor = z.infer<typeof collabPeerCursorSchema>;

/**
 * collabPresencePeerSchema is the minimal presence record a join carries: the
 * collaborator's user id and the epoch-millisecond timestamp their session
 * connected at. The color and any live selection / cursor are derived and
 * accrued client-side, so they are not part of this wire shape. Zod-first
 * because it is shared with the presence snapshot / set tool inputs and
 * validated when the collaboration session relays presence.
 */
export const collabPresencePeerSchema = z.object({
  connectedAt: z.number(),
  userId: z.string(),
});

/**
 * CollabPresencePeer is the join payload for one collaborator, derived from
 * {@link collabPresencePeerSchema}.
 */
export type CollabPresencePeer = z.infer<typeof collabPresencePeerSchema>;

/**
 * CollabPeer is one collaborator's live presence in the studio: their user id,
 * the epoch-millisecond time they connected, the deterministic palette color
 * their cursor / selection paint in, the entity they currently have focused
 * (null when nothing is), and their last-known map cursor (null when not over
 * the map). Selection reuses {@link ItinerarySelection} so a peer's focus reads
 * the exact segment/waypoint shape the local selection does.
 */
export interface CollabPeer {
  color: string;
  connectedAt: number;
  cursor: CollabPeerCursor | null;
  selection: ItinerarySelection | null;
  userId: string;
}

/**
 * CollabPeerSegmentDraft is a collaborator's in-flight SEGMENT edit relayed for
 * live preview: the peer authoring it and the live segment projection the local
 * elevation panel / map paints before the edit persists. The segment reuses the
 * SAME live-segment shape `setSegmentEditLiveSegment` carries, so the local and
 * remote previews render through one model.
 */
export interface CollabPeerSegmentDraft {
  kind: "segment";
  segment: ItinerarySegment;
  userId: string;
}

/**
 * CollabPeerZoneDraft is a collaborator's in-flight ZONE edit relayed for live
 * preview: the peer authoring it and the live drawn shape the local map paints
 * before the reshape persists. The shape reuses the SAME `Polygon` the zone edit
 * session's `draft` carries (every zone mode draws a Polygon area), so the local
 * and remote previews render through one model.
 */
export interface CollabPeerZoneDraft {
  kind: "zone";
  userId: string;
  zone: Polygon;
}

/**
 * CollabPeerDraft is a collaborator's in-flight edit relayed for live preview,
 * discriminated on the entity family it targets: a segment draft carries the live
 * segment projection, a zone draft carries the drawn Polygon. Both name
 * the authoring peer. It is trusted in-process (the collaboration session produced
 * it from a peer's live edit) rather than re-validated here, mirroring how itinerary
 * and zone geometry are cast at the API boundary; a reader narrows on `kind` to
 * reach the family-specific shape.
 */
export type CollabPeerDraft = CollabPeerSegmentDraft | CollabPeerZoneDraft;

/**
 * CollabResourceState is one editor session's live collaboration picture: the
 * connected peers keyed by user id, the live locks keyed by `"{kind}/{id}"` →
 * holder user id (so the local UI can show an entity is being edited elsewhere),
 * and the live peer drafts keyed the same way → the authoring peer + their live
 * draft (a segment projection or a zone shape). It is the inner value the
 * per-resource slice holds, so an itinerary session and a zone session each own an
 * independent copy and one session's frames never disturb the other's.
 */
export interface CollabResourceState {
  locks: Record<string, string>;
  peerDrafts: Record<string, CollabPeerDraft>;
  peers: Record<string, CollabPeer>;
}

/**
 * CollabSliceState is the receive-side collaboration picture scoped PER editor
 * resource: `collabByResource` maps a {@link collabResourceKey} (an itinerary's or
 * a zone's `"{kind}/{id}"`) onto that session's {@link CollabResourceState}, so two
 * concurrent sessions keep independent peers, locks, and drafts and neither's
 * periodic snapshot flip-flops the other's roster. It is pure presentation mirrored
 * from the collaboration session; the session is the sole writer (through the
 * collab tools), never the local editing flows.
 */
export interface CollabSliceState {
  collabByResource: Record<string, CollabResourceState>;
}

/**
 * CollabSliceActions mirror the collaboration session's frames into the store,
 * reached only through the collab tools (never a component). Every action is scoped
 * to a {@link CollabResource} — its first argument — so a frame lands in exactly its
 * editor session's slice. Presence and locks each arrive as a full authoritative
 * snapshot on (re)connect and periodically, or incrementally: presence on a single
 * join/leave (a leave drops the peer AND their live drafts), locks on a single
 * acquire/release; per-peer selection / cursor and per-entity draft each have a set
 * + clear; reset drops one resource's whole picture on that session's teardown.
 */
export interface CollabSliceActions {
  /**
   * collabApplyLockSnapshot replaces the resource's whole lock map with the
   * session's authoritative held-lock set: an entity absent from the snapshot has
   * its lock cleared, so a stale "locked by …" badge self-heals once its holder
   * departs uncleanly and drops off the periodic snapshot. Every entry is a PEER
   * lock — the session filters the local user's own locks out before dispatching.
   */
  collabApplyLockSnapshot: (resource: CollabResource, locks: readonly CollabLockEntry[]) => void;
  /**
   * collabApplyPresenceSnapshot replaces the resource's whole peer set with the
   * session's authoritative connected list: peers absent from the snapshot are
   * dropped (along with any live draft they were authoring, so a departed peer's
   * ghost cannot outlive them — their selection / cursor ride on the peer entry and
   * go with it), peers present keep their live selection / cursor (so a reconnect
   * snapshot does not flicker an active collaborator's focus), and a fresh peer
   * starts with none. The deterministic color is (re)derived from each user id.
   */
  collabApplyPresenceSnapshot: (
    resource: CollabResource,
    peers: readonly CollabPresencePeer[],
  ) => void;
  /**
   * collabClearLock releases the live lock on one entity in the resource (the
   * holder committed or cancelled their edit). Preserves the map references when no
   * lock was held so a subscriber's equality check does not re-fire on a stray clear.
   */
  collabClearLock: (resource: CollabResource, kind: CollabLockKind, id: string) => void;
  /**
   * collabClearPeerDraft drops the live draft on one entity in the resource (the
   * peer's edit ended). Preserves the map references when no draft was present.
   */
  collabClearPeerDraft: (resource: CollabResource, kind: CollabLockKind, id: string) => void;
  /**
   * collabPeerJoin adds or refreshes one collaborator in the resource from a
   * presence frame. A re-join keeps the existing live selection / cursor (a
   * duplicate join must not wipe a peer already on screen) while refreshing their
   * connected time.
   */
  collabPeerJoin: (resource: CollabResource, peer: CollabPresencePeer) => void;
  /**
   * collabPeerLeave drops a collaborator from the resource and, with them, their
   * selection / cursor (carried on the peer entry) and every live draft they were
   * authoring. Locks are left to the session's explicit lock.clear frame.
   */
  collabPeerLeave: (resource: CollabResource, userId: string) => void;
  /**
   * collabReset drops one resource's whole collaboration picture — peers, locks,
   * and drafts — on that editor session's teardown, so a stale peer never lingers
   * into the next document. It deletes only the named resource's slot, so a
   * concurrent session's live picture is untouched.
   */
  collabReset: (resource: CollabResource) => void;
  /**
   * collabSetLock records that one entity in the resource is being edited by a
   * collaborator, so the local UI can surface a "locked by …" affordance. A
   * no-op-safe upsert: re-locking to the same holder just rewrites the slot.
   */
  collabSetLock: (
    resource: CollabResource,
    kind: CollabLockKind,
    id: string,
    userId: string,
  ) => void;
  /**
   * collabSetPeerCursor updates a collaborator's live map cursor in the resource
   * (null when it leaves the map). A no-op when the peer is not tracked yet —
   * presence precedes a cursor, so an out-of-order cursor has no peer to attach to.
   */
  collabSetPeerCursor: (
    resource: CollabResource,
    userId: string,
    cursor: CollabPeerCursor | null,
  ) => void;
  /**
   * collabSetPeerDraft records a collaborator's in-flight SEGMENT projection in the
   * resource for live preview, keyed by the edited entity. Overwrites any prior
   * draft on the same entity. The zone-family analogue is
   * {@link CollabSliceActions.collabSetPeerZoneDraft}.
   */
  collabSetPeerDraft: (
    resource: CollabResource,
    kind: CollabLockKind,
    id: string,
    userId: string,
    segment: ItinerarySegment,
  ) => void;
  /**
   * collabSetPeerSelection updates a collaborator's focused entity in the resource
   * (null clears it). A no-op when the peer is not tracked yet, for the same
   * presence-first reason as the cursor.
   */
  collabSetPeerSelection: (
    resource: CollabResource,
    userId: string,
    selection: ItinerarySelection | null,
  ) => void;
  /**
   * collabSetPeerZoneDraft records a collaborator's in-flight ZONE shape in the
   * resource for live preview, keyed `zone/{id}`. It is the zone-family analogue of
   * {@link CollabSliceActions.collabSetPeerDraft}: the payload is the drawn Polygon
   * the peer is reshaping, stored in the same peer-draft map the segment drafts land
   * in so one sweep drops a departing peer's drafts of either family. Overwrites any
   * prior draft on the same zone.
   */
  collabSetPeerZoneDraft: (
    resource: CollabResource,
    id: string,
    userId: string,
    zone: Polygon,
  ) => void;
}

/**
 * makePeer builds a CollabPeer from a presence frame, carrying forward an
 * existing peer's live selection / cursor when one is supplied (a re-join or a
 * snapshot of a still-connected peer) and defaulting both to null otherwise.
 * The color is always (re)derived from the user id, so it is stable regardless.
 */
const makePeer = (peer: CollabPresencePeer, existing: CollabPeer | undefined): CollabPeer => ({
  color: collabPeerColor(peer.userId),
  connectedAt: peer.connectedAt,
  cursor: existing?.cursor ?? null,
  selection: existing?.selection ?? null,
  userId: peer.userId,
});

/**
 * withoutKey returns the record without the given key, preserving the original
 * reference when the key is absent so a no-op clear does not churn a
 * subscriber's equality check.
 */
const withoutKey = <T>(record: Record<string, T>, key: string): Record<string, T> => {
  if (!(key in record)) {
    return record;
  }

  const { [key]: _removed, ...rest } = record;

  return rest;
};

/**
 * dropDraftsByUser returns the draft map without any draft authored by the given
 * user, preserving the original reference when the user holds none so a leave
 * with no live drafts does not churn draft subscribers.
 */
const dropDraftsByUser = (
  drafts: Record<string, CollabPeerDraft>,
  userId: string,
): Record<string, CollabPeerDraft> => {
  const ownedKeys = Object.keys(drafts).filter((key) => drafts[key]?.userId === userId);
  if (ownedKeys.length === 0) {
    return drafts;
  }

  const next = { ...drafts };
  for (const key of ownedKeys) {
    delete next[key];
  }

  return next;
};

/**
 * retainDraftsByUsers returns the draft map keeping only drafts authored by a user
 * in the surviving set, preserving the original reference when every draft belongs
 * to a survivor so a snapshot that prunes nobody does not churn draft subscribers.
 * It backs the presence snapshot's ghost sweep: a peer absent from the authoritative
 * roster has departed, so their live draft must not outlive them.
 */
const retainDraftsByUsers = (
  drafts: Record<string, CollabPeerDraft>,
  userIds: ReadonlySet<string>,
): Record<string, CollabPeerDraft> => {
  const staleKeys = Object.keys(drafts).filter((key) => {
    const draft = drafts[key];

    return draft !== undefined && !userIds.has(draft.userId);
  });
  if (staleKeys.length === 0) {
    return drafts;
  }

  const next = { ...drafts };
  for (const key of staleKeys) {
    delete next[key];
  }

  return next;
};

/** EMPTY_PEERS is the shared frozen empty peer set a resource with no live picture reads as. */
const EMPTY_PEERS: Record<string, CollabPeer> = Object.freeze({});

/** EMPTY_LOCKS is the shared frozen empty lock map a resource with no live picture reads as. */
const EMPTY_LOCKS: Record<string, string> = Object.freeze({});

/** EMPTY_PEER_DRAFTS is the shared frozen empty draft map a resource with no live picture reads as. */
const EMPTY_PEER_DRAFTS: Record<string, CollabPeerDraft> = Object.freeze({});

/**
 * EMPTY_RESOURCE_STATE is the stable empty picture a resource with no mirrored
 * frames yet resolves to — its three maps are the shared frozen empties, so a
 * selector read against an absent resource returns reference-stable defaults a
 * subscriber's equality check never churns on. The setters never mutate it: every
 * write spreads the current maps into fresh objects, so the frozen empties stay
 * pristine.
 */
const EMPTY_RESOURCE_STATE: CollabResourceState = {
  locks: EMPTY_LOCKS,
  peerDrafts: EMPTY_PEER_DRAFTS,
  peers: EMPTY_PEERS,
};

/**
 * resourceStateOf reads one resource's live collaboration picture, returning the
 * shared stable empty default when the resource has no mirrored frames yet — so a
 * setter can spread the current maps whether or not the resource already exists.
 */
const resourceStateOf = (
  byResource: Record<string, CollabResourceState>,
  key: string,
): CollabResourceState => byResource[key] ?? EMPTY_RESOURCE_STATE;

/**
 * withResource returns collabByResource with one resource's picture replaced,
 * scoping a write to that session without disturbing any other resource's slot.
 */
const withResource = (
  byResource: Record<string, CollabResourceState>,
  key: string,
  next: CollabResourceState,
): Record<string, CollabResourceState> => ({ ...byResource, [key]: next });

/**
 * collabInitialState seeds an empty per-resource map — the studio opens with no
 * collaboration picture for any resource until a session relays its first frame.
 */
export const collabInitialState: CollabSliceState = {
  collabByResource: {},
};

/**
 * createCollabSlice builds the receive-side collaboration setters. Every action
 * mirrors a collaboration-session frame into the store, scoped to the resource it
 * names, so an itinerary session and a zone session keep independent pictures; the
 * presence, lock, and draft maps within one resource stay independent so a lock
 * frame never disturbs a cursor and a draft frame never disturbs presence.
 */
export const createCollabSlice: StateCreator<
  CollabSliceState & CollabSliceActions,
  [],
  [],
  CollabSliceActions
> = (set) => ({
  collabApplyLockSnapshot: (resource, locks) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const nextLocks: Record<string, string> = {};
      for (const lock of locks) {
        nextLocks[collabLockKey(lock.kind, lock.id)] = lock.userId;
      }

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          locks: nextLocks,
        }),
      };
    }),

  collabApplyPresenceSnapshot: (resource, peers) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const nextPeers: Record<string, CollabPeer> = {};
      const survivors = new Set<string>();
      for (const peer of peers) {
        nextPeers[peer.userId] = makePeer(peer, current.peers[peer.userId]);
        survivors.add(peer.userId);
      }

      // A peer dropped from the authoritative roster has departed: sweep any live
      // draft they were authoring so a ghost preview cannot outlive them (their
      // selection / cursor ride on the peer entry and are already gone with it).
      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peerDrafts: retainDraftsByUsers(current.peerDrafts, survivors),
          peers: nextPeers,
        }),
      };
    }),

  collabClearLock: (resource, kind, id) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const locks = withoutKey(current.locks, collabLockKey(kind, id));
      if (locks === current.locks) {
        return {};
      }

      return { collabByResource: withResource(state.collabByResource, key, { ...current, locks }) };
    }),

  collabClearPeerDraft: (resource, kind, id) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const peerDrafts = withoutKey(current.peerDrafts, collabLockKey(kind, id));
      if (peerDrafts === current.peerDrafts) {
        return {};
      }

      return {
        collabByResource: withResource(state.collabByResource, key, { ...current, peerDrafts }),
      };
    }),

  collabPeerJoin: (resource, peer) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peers: { ...current.peers, [peer.userId]: makePeer(peer, current.peers[peer.userId]) },
        }),
      };
    }),

  collabPeerLeave: (resource, userId) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const peers = withoutKey(current.peers, userId);
      const peerDrafts = dropDraftsByUser(current.peerDrafts, userId);
      if (peers === current.peers && peerDrafts === current.peerDrafts) {
        return {};
      }

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peerDrafts,
          peers,
        }),
      };
    }),

  collabReset: (resource) =>
    set((state) => {
      const collabByResource = withoutKey(state.collabByResource, collabResourceKey(resource));
      if (collabByResource === state.collabByResource) {
        return {};
      }

      return { collabByResource };
    }),

  collabSetLock: (resource, kind, id, userId) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          locks: { ...current.locks, [collabLockKey(kind, id)]: userId },
        }),
      };
    }),

  collabSetPeerCursor: (resource, userId, cursor) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const peer = current.peers[userId];
      if (!peer) {
        return {};
      }

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peers: { ...current.peers, [userId]: { ...peer, cursor } },
        }),
      };
    }),

  collabSetPeerDraft: (resource, kind, id, userId, segment) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peerDrafts: {
            ...current.peerDrafts,
            // The payload is always a segment projection, so the stored draft is
            // the segment variant; `kind`/`id` only compose the map key.
            [collabLockKey(kind, id)]: { kind: "segment", segment, userId },
          },
        }),
      };
    }),

  collabSetPeerSelection: (resource, userId, selection) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);
      const peer = current.peers[userId];
      if (!peer) {
        return {};
      }

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peers: { ...current.peers, [userId]: { ...peer, selection } },
        }),
      };
    }),

  collabSetPeerZoneDraft: (resource, id, userId, zone) =>
    set((state) => {
      const key = collabResourceKey(resource);
      const current = resourceStateOf(state.collabByResource, key);

      return {
        collabByResource: withResource(state.collabByResource, key, {
          ...current,
          peerDrafts: {
            ...current.peerDrafts,
            [collabLockKey("zone", id)]: { kind: "zone", userId, zone },
          },
        }),
      };
    }),
});

/**
 * selectCollabPeers reads one resource's connected peers, returning the shared
 * frozen empty set when that session has no live picture — so a component
 * subscribing through useShallow (or a plain reference check) settles on a stable
 * default rather than churning on a fresh `{}` each store change. The factory takes
 * the resource so a surface reads exactly its own session's roster.
 */
export const selectCollabPeers =
  (resource: CollabResource) =>
  (state: CollabSliceState): Record<string, CollabPeer> =>
    state.collabByResource[collabResourceKey(resource)]?.peers ?? EMPTY_PEERS;

/**
 * selectCollabPeerDrafts reads one resource's live peer drafts, returning the
 * shared frozen empty map when that session has no live picture, so a draft
 * subscriber's reference check does not churn. It is the per-resource source the
 * elevation panel and the map peer-ghost fold, paired with {@link peerSegmentDraftsOf}.
 */
export const selectCollabPeerDrafts =
  (resource: CollabResource) =>
  (state: CollabSliceState): Record<string, CollabPeerDraft> =>
    state.collabByResource[collabResourceKey(resource)]?.peerDrafts ?? EMPTY_PEER_DRAFTS;

/**
 * selectCollabLockHolder reads the collaborator holding one entity's soft-lock in
 * a resource's session, or undefined when the entity is free. Narrow by design so a
 * locked-affordance consumer re-renders only when THAT entity's holder changes, not
 * on every unrelated frame; the factory takes the resource + entity so the read is
 * a single scoped map lookup.
 */
export const selectCollabLockHolder =
  (resource: CollabResource, kind: CollabLockKind, id: string) =>
  (state: CollabSliceState): string | undefined =>
    state.collabByResource[collabResourceKey(resource)]?.locks[collabLockKey(kind, id)];

/**
 * peerSegmentDraftsOf extracts the live SEGMENT drafts from a peer-draft map —
 * the in-flight ItinerarySegment projections. It is the pure projection the
 * elevation panel folds onto the open itinerary so a peer's in-progress reshape
 * morphs the profile the same way a local edit does; a zone draft carries a
 * different geometry shape (a drawn Polygon) and is skipped by the `kind`
 * narrow. Pure and allocation-light so a render memo can call it once per peer frame
 * off the stable draft-map reference.
 */
export const peerSegmentDraftsOf = (
  drafts: Record<string, CollabPeerDraft>,
): ItinerarySegment[] => {
  const segments: ItinerarySegment[] = [];
  for (const draft of Object.values(drafts)) {
    if (draft.kind === "segment") {
      segments.push(draft.segment);
    }
  }

  return segments;
};
